// Códigos simples para documento fiscal (factura/nota)
import { FiscalSellerData } from './fiscal-seller.model';

export enum SmartDocumentTypeCode {
    Factura = 1,
    NotaDebito = 2,
    NotaCredito = 3,
}

/** Cuerpo para POST /api/smart/reportz (incluye tenantId para el backend). */
export interface SmartReportZPayload {
    tenantId: string;
    rif: string;
    fechaEmision: string;
    sucursal?: string;
}

/** Payload en español (referencia doc SMART); el backend espera DTO en inglés vía `SmartRetentionRequestDto`. */
export interface SmartRetentionItem {
    codigo: string;
    descripcion: string;
    numerocontrol: string;
    numerofactura: string;
    numerocredito?: string;
    numerodebito?: string;
    relacionado?: string;
    fecha: string;
    total: number;
    exento: number;
    base: number;
    sustraendopn?: number;
    alicuota: number;
    impuesto: number;
    porcentaje: number;
    montoretenido: number;
}

export interface SmartRetentionPayload {
    rif: string;
    numerocomprobante: string;
    nombrecliente: string;
    rifcedulacliente: string;
    idtipocedulacliente: number;
    emailcliente: string;
    direccioncliente: string;
    telefonocliente: string;
    idtipocomprobante: number;
    exento: number;
    base: number;
    impuesto: number;
    montoretenido: number;
    total: number;
    sendmail: number;
    fechaemision: string;
    fechaentrega: string;
    mesfiscal: string;
    anniofiscal: string;
    cuerporetencion: SmartRetentionItem[];
}

/** Cuerpo para POST /api/smart/retention (class-validator en API). */
export interface SmartRetentionItemRequest {
    code: string;
    description: string;
    controlNumber: string;
    invoiceNumber: string;
    creditNoteNumber?: string;
    debitNoteNumber?: string;
    relatedDocument?: string;
    date: string;
    total: number;
    exento: number;
    base: number;
    sustraendoPn?: number;
    rate: number;
    tax: number;
    percentage: number;
    retained: number;
}

export interface SmartRetentionRequestDto {
    tenantId: string;
    rif: string;
    comprobanteNumber: string;
    clientName: string;
    clientDni: string;
    clientDniType: number;
    email: string;
    address: string;
    phone: string;
    comprobanteType: number;
    exento: number;
    base: number;
    tax: number;
    retained: number;
    total: number;
    issueDate: string;
    deliveryDate: string;
    fiscalMonth: string;
    fiscalYear: string;
    items: SmartRetentionItemRequest[];
}

export interface SmartDocumentContext {
    bill: any;
    products: any[];
    customer: any;
    commerce: any;
    paymentMethods: any[];
}

export interface SmartDocumentOptions {
    documentType?: SmartDocumentTypeCode;
    numeroFacturaAfectada?: string;
    scope?: 'digital' | 'reportes' | 'libre' | 'imprFiscal';
}

export interface SmartApiResponse {
    numeroControl?: string;
    numeroInterno?: string;
    fechaRegistro?: string;
    raw?: any;
}

// Respuesta estándar del backend /api/smart/*
export interface SmartBackendResponse {
    success?: boolean;
    message?: string;
    scope?: string;
    data?: any;
    payload?: any;
}

/**
 * Tope de ítems por documento de la plantilla UniDigital (verificado en la cuenta Fina: con 30 el
 * proveedor rechaza). El backend lo valida igual en `unidigital.constants.ts`; acá vive una sola
 * copia para que el aviso del carrito, el bloqueo al facturar y los textos no se desalineen.
 */
export const UNIDIGITAL_MAX_LINE_ITEMS = 25;

/**
 * `data` cuando el documento salió por UniDigital (proveedor digital nuevo).
 * A diferencia de Smart, el número de control puede llegar DESPUÉS de emitir
 * (status `awaiting_control_number`, asignación en ~5-7 min vía worker del backend).
 */
export interface UnidigitalEmissionData {
    provider: 'UNIDIGITAL';
    status: 'awaiting_control_number' | 'success';
    providerDocumentId: string;
    documentNumber: string;
    serie: string;
    controlNumber?: string;
    controlNumberFormatted?: string;
    pdfUrl?: string;
    alreadyEmitted?: boolean;
    message: string;
}

/** Serie disponible en la cuenta UniDigital del tenant (el backend resuelve name → strongId al emitir). */
export interface UnidigitalSerieSummary {
    name: string;
    strongId: string;
}

/** Resumen (sin credenciales) de la configuración UniDigital del tenant. */
export interface UnidigitalIntegrationSummary {
    userName?: string;
    hasCredentials?: boolean;
    baseUrl?: string | null;
    /** Serie por defecto (usada cuando no se envía `serie` al emitir). */
    serieStrongId?: string | null;
    serieName?: string | null;
    sucursalStrongId?: string | null;
    templateName?: string | null;
    /** Series disponibles de la cuenta; con más de una, el cajero elige al emitir. */
    series?: UnidigitalSerieSummary[] | null;
}

export interface SmartAnnulRequest {
    billId: string;
    tenantId: string;
    /** Documento a anular; default 'invoice'. */
    target?: 'invoice' | 'creditNote' | 'debitNote';
    noteIndex?: number;
}

export interface SmartRefreshStatusRequest {
    billId: string;
    tenantId: string;
}

export interface SmartImprFiscalPrinter extends FiscalSellerData {
    printerKey?: string;
    enabled?: boolean;
}

export interface SmartApiError {
    code?: string | number;
    message: string;
    httpStatus?: number;
    details?: any;
}

// Payloads mínimos para delegar el mapeo en el backend
export interface SmartBackendInvoiceRequest {
    billId: string;
    tenantId: string;
    documentType?: SmartDocumentTypeCode; // default factura
    fiscalSeller?: FiscalSellerData | null;
    serie?: string; // forma libre con serialización, o serie elegida en UniDigital multi-serie (backend resuelve nombre → strongId; ausente = serie default)
    controlNumber?: string; // número de control forma libre (00-XXXXXXXX)
}

export interface NoteItemPayload {
    description: string;
    quantity: number;
    unitPrice: number;
    ivaPercent?: number;
    code?: string;
    unit?: string;
}

export interface SmartBackendNoteRequest extends SmartBackendInvoiceRequest {
    documentType: SmartDocumentTypeCode.NotaDebito | SmartDocumentTypeCode.NotaCredito;
    relatedBillId: string;
    numeroFacturaAfectada?: string;
    fechaFacturaAfectada?: string;
    montoFacturaAfectada?: number;
    comentarioFacturaAfectada?: string;
    regimenAnterior?: number;
    items?: NoteItemPayload[];
    igtf?: boolean;
    totalIGTF?: number;
    paymentMethods?: any[];
    returnMethods?: any[];
    /** Valida el payload de la nota en el backend sin emitirla ni consumir correlativo. */
    dryRun?: boolean;
}

/**
 * FINA-3326: origen de una nota fiscal persistida en `fiscal.creditNotes|debitNotes`
 * ('return' devolución/cambio, 'differential' diferencial cambiario, 'differential-reversal' su
 * reversa en devolución total). Ausente ⇒ nota legado.
 */
export type FiscalNoteKind = 'return' | 'differential' | 'differential-reversal';

/** FINA-3326: reversa de la nota de diferencial en devolución total; `dryRun` valida sin emitir. */
export interface SmartBackendDifferentialReversalRequest extends SmartBackendInvoiceRequest {
    dryRun?: boolean;
}

/** FINA-3326: respuesta de POST /smart/differential-reversal-preview (solo lectura). */
export interface DifferentialReversalPreview {
    applies: boolean;
    /** Tipo de la nota espejo: NC si la de diferencial fue ND y viceversa. */
    documentType: SmartDocumentTypeCode.NotaCredito | SmartDocumentTypeCode.NotaDebito | null;
    /** Tipo del documento afectado: siempre la factura (SENIAT: una nota no puede afectar a otra nota). */
    affectedDocumentType?: number;
    /** Control/ticket de la FACTURA, que es el documento afectado que declara la reversa. */
    affectedControlNumber?: string;
    /** Control/ticket de la nota de diferencial que se reversa (trazabilidad; va en el comentario). */
    reversedNoteReference?: string;
    provider?: 'SMART' | 'UNIDIGITAL';
    totalVES?: number;
    /** Tasa de la factura (la reversa conserva la rFact de la nota de diferencial). */
    rFact?: number;
    /** Líneas de la nota de diferencial (para el PDF local de forma libre). */
    items?: NoteItemPayload[];
    reason?: string;
}

/**
 * Mensaje de error accionable para el cajero (hallazgo H-01 de QA): el backend responde
 * 4xx con `{ success: false, message: '...' }`, pero `HttpErrorResponse.message` de Angular
 * es el genérico "Http failure response for <url>: 422 OK" — que además expone la URL interna
 * del API. Se prefiere siempre el cuerpo del backend y solo se cae al fallback provisto.
 */
export function extractFiscalApiErrorMessage(error: any, fallback: string): string {
    const backendMessage = error?.error?.message || error?.error?.title;
    if (typeof backendMessage === 'string' && backendMessage.trim()) return backendMessage;
    // Errores ya normalizados (no HttpErrorResponse): usar message solo si no es el genérico HTTP.
    const message = typeof error?.message === 'string' ? error.message : '';
    if (message && !/^Http failure response/i.test(message)) return message;
    return fallback;
}

/** 422 de las rutas de emisión (factura, NC/ND, diferencial) cuando el comercio no tiene saldo de números de control (ADM-732). */
export const CONTROL_NUMBERS_EXHAUSTED_CODE = 'CONTROL_NUMBERS_EXHAUSTED';

/**
 * FINA-3330: 422 de UniDigital cuando una factura de un comercio que no es contribuyente especial lleva
 * pagos en divisa sin IGTF (regla `PaymentsMustBeValid` del proveedor). El backend manda título y mensaje
 * accionables; el toast los muestra tal cual.
 */
export const UNIDIGITAL_IGTF_REQUIRED_FOR_FOREIGN_PAYMENTS_CODE = 'UNIDIGITAL_IGTF_REQUIRED_FOR_FOREIGN_PAYMENTS';

/** Códigos cuyo `title` del backend se prefiere al del sitio. */
const BACKEND_TITLE_CODES: ReadonlyArray<string> = [CONTROL_NUMBERS_EXHAUSTED_CODE, UNIDIGITAL_IGTF_REQUIRED_FOR_FOREIGN_PAYMENTS_CODE];

export interface FiscalEmissionError {
    title: string;
    message: string;
    /** Sin saldo de números de control: no es un rechazo de la imprenta; la venta se factura al reponer saldo. */
    exhausted: boolean;
}

/**
 * Error de emisión fiscal listo para mostrar. Los catch de emisión (`invoice`, devoluciones,
 * diferencial) lo usan en vez de leer `error.message`, que en un `HttpErrorResponse` es el
 * genérico de Angular. Una sola semántica de título: el `title` del backend solo con los códigos de
 * `BACKEND_TITLE_CODES` (`CONTROL_NUMBERS_EXHAUSTED`, `UNIDIGITAL_IGTF_REQUIRED_FOR_FOREIGN_PAYMENTS`);
 * en el resto, el `fallbackTitle` del sitio.
 */
export function resolveFiscalEmissionError(error: any, fallback: { fallbackTitle: string; fallbackMessage: string }): FiscalEmissionError {
    const code = error?.error?.code;
    const exhausted = code === CONTROL_NUMBERS_EXHAUSTED_CODE;
    const backendTitle = typeof error?.error?.title === 'string' ? error.error.title.trim() : '';
    const preferBackendTitle = typeof code === 'string' && BACKEND_TITLE_CODES.includes(code);
    return {
        title: preferBackendTitle && backendTitle ? backendTitle : fallback.fallbackTitle,
        message: extractFiscalApiErrorMessage(error, fallback.fallbackMessage),
        exhausted,
    };
}
